import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Download, Share, Plus, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

const isStandalone = () =>
  typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const InstallAppButton = ({ variant = 'nav' }) => {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const onBip = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setInstalled(true); setPrompt(null); toast.success('CalorieSnap added to your home screen!'); };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') { setPrompt(null); }
      return;
    }
    if (isIOS()) { setIosOpen(true); return; }
    // Desktop browser without install support yet
    toast.info('Open this site on your phone to add it to the home screen.');
  };

  // Hide the button on desktop when there's no install prompt AND not iOS (nothing useful to do)
  const hideOnDesktop = !prompt && !isIOS() && variant === 'nav';
  if (hideOnDesktop) return null;

  const label = variant === 'hero' ? 'Add to home screen' : 'Install app';

  return (
    <>
      <Button
        onClick={handleClick}
        variant={variant === 'hero' ? 'default' : 'outline'}
        className={variant === 'hero'
          ? 'bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-11 px-5 font-semibold'
          : 'rounded-full border-emerald-950/10 hover:bg-emerald-50 text-emerald-950 h-9 px-4'}
      >
        {variant === 'hero' ? <Smartphone className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
        {label}
      </Button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-2"><Smartphone className="w-6 h-6" /></div>
            <DialogTitle className="font-display text-2xl text-emerald-950">Add CalorieSnap to your Home Screen</DialogTitle>
            <DialogDescription>Two quick taps in Safari and you’re done.</DialogDescription>
          </DialogHeader>
          <ol className="space-y-4 pt-2">
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-emerald-700 text-white grid place-items-center text-sm font-bold shrink-0">1</span>
              <div>
                <p className="text-emerald-950 font-medium">Tap the Share button</p>
                <p className="text-emerald-950/60 text-sm mt-0.5 inline-flex items-center gap-1">Look for <Share className="w-3.5 h-3.5" /> at the bottom of Safari</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 rounded-full bg-emerald-700 text-white grid place-items-center text-sm font-bold shrink-0">2</span>
              <div>
                <p className="text-emerald-950 font-medium">Choose “Add to Home Screen”</p>
                <p className="text-emerald-950/60 text-sm mt-0.5 inline-flex items-center gap-1">Scroll and tap the <Plus className="w-3.5 h-3.5" /> option, then Add</p>
              </div>
            </li>
          </ol>
          <p className="text-xs text-emerald-950/50 pt-3">Tip: this only works in Safari on iPhone/iPad, not in Chrome or in-app browsers.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
